import type { ReactNode } from 'react'
import React, { useMemo, createContext, useContext, useEffect } from 'react'
import type { FamilyMembers } from '@fs/zion-tree-types'
import type { KeyedMutator } from 'swr'
import useFocusFamilyMembers from '../../data-service/useFocusFamilyMembers'
import { useExpandedSections } from '../../providers/ExpandedSectionsProvider'

type FocusFamilyMembersContextValue = {
  focusFamilyMembers: FamilyMembers
  loadingFamilyMembers: boolean
  refetchFamilyMembers: KeyedMutator<FamilyMembers>
}

const FocusFamilyMembersContext = createContext<FocusFamilyMembersContextValue>({
  focusFamilyMembers: { spouses: [], parents: [] },
  loadingFamilyMembers: true,
  refetchFamilyMembers: () => Promise.resolve(undefined),
})

type Props = {
  children: ReactNode
}

export default function FocusFamilyMembersProvider({ children }: Props): JSX.Element {
  const expandedStates = useExpandedSections()[0]
  const familyMembersCardExpanded = expandedStates.family || expandedStates['research-help']

  const { focusFamilyMembers, isLoading, mutate } = useFocusFamilyMembers()
  const value = useMemo(
    () => ({
      focusFamilyMembers,
      refetchFamilyMembers: mutate,
      loadingFamilyMembers: isLoading,
    }),
    [focusFamilyMembers, isLoading, mutate]
  )

  useEffect(() => {
    if (familyMembersCardExpanded) mutate()
  }, [familyMembersCardExpanded, mutate])

  return <FocusFamilyMembersContext.Provider value={value}>{children}</FocusFamilyMembersContext.Provider>
}

export const useFocusFamilyMembersContext = (): FocusFamilyMembersContextValue => {
  return useContext(FocusFamilyMembersContext)
}
